import React, { useState, useEffect } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { userPreferencesService } from '../../../services/supabaseService';
import { useAuth } from '../../../contexts/AuthContext';

const CommunicationPreferences = ({ userRole, onSave }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    preferredContactMethod: 'email',
    responseTime: '24-hours',
    availableHours: {
      start: '09:00',
      end: '17:00'
    },
    timezone: 'America/Toronto',
    autoReply: false,
    autoReplyMessage: '',
    allowWeekendContact: false,
    phoneNumber: '',
    linkedIn: '',
    preferredLanguage: 'en'
  });

  useEffect(() => {
    const loadSettings = async () => {
      if (!user) return;

      try {
        const prefs = await userPreferencesService?.get(user?.id);
        if (prefs?.communicationSettings) {
          setSettings(prefs?.communicationSettings);
        }
      } catch (err) {
        console.error('Error loading communication settings:', err);
      }
    };

    loadSettings();
  }, [user]);

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleSelectChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleInputChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      await userPreferencesService?.upsert(user?.id, {
        communicationSettings: settings
      });
      onSave?.('Communication preferences saved successfully');
    } catch (err) {
      console.error('Error saving communication settings:', err);
    }
  };

  const responseTimeOptions = [
    { value: '1hour', label: 'Within 1 hour' },
    { value: '4hours', label: 'Within 4 hours' },
    { value: '24hours', label: 'Within 24 hours' },
    { value: '48hours', label: 'Within 48 hours' },
    { value: 'flexible', label: 'Flexible' }
  ];

  const contactMethodOptions = [
    { value: 'platform', label: 'BlinD Platform Only' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'video', label: 'Video Call' }
  ];

  const timezoneOptions = [
    { value: 'EST', label: 'Eastern Time (EST)' },
    { value: 'CST', label: 'Central Time (CST)' },
    { value: 'MST', label: 'Mountain Time (MST)' },
    { value: 'PST', label: 'Pacific Time (PST)' },
    { value: 'AST', label: 'Atlantic Time (AST)' }
  ];

  const languageOptions = [
    { value: 'english', label: 'English' },
    { value: 'french', label: 'French' },
    { value: 'bilingual', label: 'Bilingual (English/French)' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-2">Communication Preferences</h2>
        <p className="text-sm text-[#6B7280]">
          Set your availability and preferred methods of communication
        </p>
      </div>

      {/* Response Expectations */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Clock" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Response Time</h3>
        </div>
        
        <Select
          label="Expected response time"
          description="Let others know when they can expect a response from you"
          options={responseTimeOptions}
          value={settings?.responseTime}
          onChange={(value) => handleSelectChange('responseTime', value)}
        />

        <Checkbox
          checked={settings?.autoReply}
          onChange={() => handleToggle('autoReply')}
          label="Enable auto-reply"
          description="Send an automatic response when you receive a message"
        />

        {settings?.autoReply && (
          <div className="ml-6">
            <Input
              label="Auto-reply message"
              placeholder="Thank you for your message. I'll respond within 24 hours."
              value={settings?.autoReplyMessage}
              onChange={(e) => handleInputChange('autoReplyMessage', e?.target?.value)}
            />
          </div>
        )}
      </div>

      {/* Availability Schedule */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Calendar" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Availability Schedule</h3>
        </div>
        
        <div className="space-y-2">
          <p className="text-sm text-[#6B7280] mb-3">Select the days you're available for communication</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: 'availableMonday', label: 'Monday' },
              { key: 'availableTuesday', label: 'Tuesday' },
              { key: 'availableWednesday', label: 'Wednesday' },
              { key: 'availableThursday', label: 'Thursday' },
              { key: 'availableFriday', label: 'Friday' },
              { key: 'availableSaturday', label: 'Saturday' },
              { key: 'availableSunday', label: 'Sunday' }
            ]?.map((day) => (
              <button
                key={day?.key}
                onClick={() => handleToggle(day?.key)}
                className={`px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all ${
                  settings?.[day?.key]
                    ? 'bg-[#5B2DFF] border-[#5B2DFF] text-white'
                    : 'bg-white border-[#E5E7EB] text-[#6B7280] hover:border-[#5B2DFF]'
                }`}
              >
                {day?.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Input
            type="time"
            label="Available from"
            value={settings?.startTime}
            onChange={(e) => handleInputChange('startTime', e?.target?.value)}
          />
          <Input
            type="time"
            label="Available until"
            value={settings?.endTime}
            onChange={(e) => handleInputChange('endTime', e?.target?.value)}
          />
          <Select
            label="Timezone"
            options={timezoneOptions}
            value={settings?.timezone}
            onChange={(value) => handleSelectChange('timezone', value)}
          />
        </div>
      </div>

      {/* Contact Methods */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Phone" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Preferred Contact Methods</h3>
        </div>
        
        <Select
          label="Primary contact method"
          description="Your preferred way to be contacted"
          options={contactMethodOptions}
          value={settings?.preferredMethod}
          onChange={(value) => handleSelectChange('preferredMethod', value)}
        />

        <div className="space-y-3 mt-4">
          <p className="text-sm font-medium text-[#111827]">Allow these contact methods:</p>
          
          <Checkbox
            checked={settings?.allowVideoCall}
            onChange={() => handleToggle('allowVideoCall')}
            label="Video calls"
            description="Allow video interview requests"
          />

          <Checkbox
            checked={settings?.allowPhoneCall}
            onChange={() => handleToggle('allowPhoneCall')}
            label="Phone calls"
            description="Allow phone interview requests"
          />
        </div>
      </div>

      {/* Language Preferences */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Globe" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Language</h3>
        </div>
        
        <Select
          label="Preferred communication language"
          options={languageOptions}
          value={settings?.preferredLanguage}
          onChange={(value) => handleSelectChange('preferredLanguage', value)}
        />
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={24} color="#5B2DFF" />
          <div>
            <h4 className="font-semibold text-[#111827] mb-2">Your Communication Summary</h4>
            <ul className="space-y-1 text-sm text-[#6B7280]">
              <li>• Response time: {responseTimeOptions?.find(o => o?.value === settings?.responseTime)?.label}</li>
              <li>• Available: {settings?.startTime} - {settings?.endTime} {settings?.timezone}</li>
              <li>• Preferred method: {contactMethodOptions?.find(o => o?.value === settings?.preferredMethod)?.label}</li>
              <li>• Language: {languageOptions?.find(o => o?.value === settings?.preferredLanguage)?.label}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="pt-6 border-t border-[#E5E7EB] flex justify-end">
        <Button
          onClick={handleSave}
          iconName="Save"
          className="bg-gradient-to-r from-[#5B2DFF] to-[#7C3FFF]"
        >
          Save Communication Preferences
        </Button>
      </div>
    </div>
  );
};

export default CommunicationPreferences;